/**
 * SatelliteViewerMolecule - Paginated horizontal carousel of satellite nodes
 *
 * Displays satellites of a selected node as a horizontal row of circular nodes
 * positioned at the bottom-left of the orbital view. Shows up to 5 satellites
 * per page with arrow navigation and page indicators.
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import type { Sphere, SatelliteSphere } from 'types/global';
import type { NodeData } from '../OrbitalVisualization/constants';
import SatelliteCardAtom from '../../atoms/SatelliteCard/SatelliteCardAtom';
import './SatelliteViewerMolecule.scss';

export interface SatelliteViewerMoleculeProps {
  /** Whether the panel is visible */
  visible: boolean;
  /** Close callback */
  onHide: () => void;
  /** The parent node whose satellites are being displayed */
  parentNode: NodeData | null;
  /** Callback when a satellite is double-clicked (navigates into it) */
  onSatelliteDoubleClick?: (satellite: SatelliteSphere, sphere?: Sphere) => void;
  /** Source position of the clicked node for animation */
  sourcePosition?: { x: number; y: number };
  /** Additional CSS class */
  className?: string;
  /** Items per page (default: 5) */
  itemsPerPage?: number;
}

/**
 * Convert SatelliteSphere to a minimal Sphere object for SatelliteCardAtom
 */
function satelliteToSphere(satellite: SatelliteSphere): Sphere {
  return {
    id: satellite.id,
    label: 'sphere',
    glyph: satellite.glyph,
    glyphColor: satellite.glyphColor,
    properties: {
      type: 'satellite',
      name: satellite.name,
    },
    status: {
      id: `status-${satellite.id}`,
      currentValue: {
        status: satellite.status,
        perspective: null,
        dimensions: [],
        factors: [],
      },
      computedAt: new Date().toISOString(),
      version: 1,
    },
  } as Sphere;
}

const SatelliteViewerMolecule: React.FC<SatelliteViewerMoleculeProps> = ({
  visible,
  onHide,
  parentNode,
  onSatelliteDoubleClick,
  sourcePosition,
  className = '',
  itemsPerPage = 5,
}) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);

  const satellites = useMemo(() => parentNode?.satellites || [], [parentNode]);

  // Calculate pagination
  const totalPages = Math.ceil(satellites.length / itemsPerPage);
  const startIndex = currentPage * itemsPerPage;
  const visibleSatellites = satellites.slice(startIndex, startIndex + itemsPerPage);
  const showNavigation = satellites.length > itemsPerPage;

  // Reset to first page when parent node changes
  useEffect(() => {
    setCurrentPage(0);
  }, [parentNode?.id]);

  // Navigation handlers
  const handlePrevPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1));
  }, [totalPages]);

  // Handle satellite double-click (navigate into satellite)
  const handleSatelliteDoubleClick = (satellite: SatelliteSphere) => {
    if (onSatelliteDoubleClick) {
      const sphere = satelliteToSphere(satellite);
      onSatelliteDoubleClick(satellite, sphere);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    if (!visible || !showNavigation) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevPage();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNextPage();
      }
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    return () => globalThis.removeEventListener('keydown', handleKeyDown);
  }, [visible, showNavigation, handlePrevPage, handleNextPage]);

  if (!parentNode || !visible || satellites.length === 0) return null;

  return (
    <div className={`satellite-viewer-molecule ${className}`}>
      <div className="satellite-viewer-molecule__dock">
        {/* Left Navigation Arrow */}
        {showNavigation && (
          <button
            className={`satellite-viewer-molecule__nav-button satellite-viewer-molecule__nav-button--prev ${currentPage === 0 ? 'satellite-viewer-molecule__nav-button--disabled' : ''}`}
            onClick={handlePrevPage}
            aria-label="Previous page"
            title="Previous page"
            disabled={currentPage === 0}
          >
            <i className="pi pi-chevron-left" />
          </button>
        )}

        {/* Satellites Container */}
        <div
          className="satellite-viewer-molecule__container"
          key={currentPage}
        >
          {visibleSatellites.map((satellite, index) => (
            <div
              key={satellite.id || `satellite-${startIndex + index}`}
              className="satellite-viewer-molecule__node"
              style={{
                animationDelay: `${index * 80}ms`,
                ...(sourcePosition && {
                  '--source-x': `${sourcePosition.x}px`,
                  '--source-y': `${sourcePosition.y}px`,
                } as React.CSSProperties),
              }}
            >
              <SatelliteCardAtom
                satellite={satellite}
                sphere={satelliteToSphere(satellite)}
                size={50}
                onDoubleClick={() => handleSatelliteDoubleClick(satellite)}
                className="satellite-viewer-molecule__card"
              />
            </div>
          ))}
        </div>

        {/* Right Navigation Arrow */}
        {showNavigation && (
          <button
            className={`satellite-viewer-molecule__nav-button satellite-viewer-molecule__nav-button--next ${currentPage >= totalPages - 1 ? 'satellite-viewer-molecule__nav-button--disabled' : ''}`}
            onClick={handleNextPage}
            aria-label="Next page"
            title="Next page"
            disabled={currentPage >= totalPages - 1}
          >
            <i className="pi pi-chevron-right" />
          </button>
        )}
      </div>

      {/* Page Indicators */}
      {showNavigation && totalPages > 1 && (
        <div className="satellite-viewer-molecule__indicators">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i}
              className={`satellite-viewer-molecule__indicator ${
                i === currentPage ? 'satellite-viewer-molecule__indicator--active' : ''
              }`}
              onClick={() => setCurrentPage(i)}
              aria-label={`Go to page ${i + 1}`}
              title={`Page ${i + 1} of ${totalPages}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default SatelliteViewerMolecule;
